// @ts-nocheck
import * as React from "react";
import { useLoaderData } from "react-router-dom";

import Container from "@mui/material/Container";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import FilterAltIcon from "@mui/icons-material/FilterAlt";

import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";

import Header from "../components/Header";
import { listAnimals } from "../models/animals";
import { listAssessments } from "../models/assessments";
import { listChanges } from "../models/changes";
import { listFinalEvaluations } from "../models/finalevaluation";
import { listSectionAs } from "../models/sectiona";
import { listSectionBs } from "../models/sectionb";
import { categoryList, typeList, statusList } from "../models/categories";
import { date, dayjs, today } from "../utils/datetime";
import { listLabel } from "../utils/lists";
import { sectionBBehaviors } from "../utils/sectionBBehaviors";
import {
  averageAdjectiveComposite,
  sectionBBehaviorFrequencyPercents,
} from "../utils/sections";
import { useForm, FormProvider, useWatch } from "react-hook-form";
import Form from "../components/form/Form";
import FormTextField from "../components/form/FormTextField";
import FormMultiSelectChips from "../components/form/FormMultiSelectChips";
import FormMultiSelectChipsFilter from "../components/form/FormMultiSelectChipsFilter";
import { name as animalName } from "../utils/animal";
import { name as speciesName } from "../utils/species";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Tooltip, Legend);

const palette = [
  "#1f77b4",
  "#ff7f0e",
  "#2ca02c",
  "#d62728",
  "#9467bd",
  "#8c564b",
  "#e377c2",
  "#7f7f7f",
  "#bcbd22",
  "#17becf",
];
const changeRangePalette = [
  "rgba(2, 132, 199, 0.12)",
  "rgba(22, 163, 74, 0.12)",
  "rgba(217, 119, 6, 0.12)",
  "rgba(220, 38, 38, 0.12)",
  "rgba(124, 58, 237, 0.12)",
  "rgba(8, 145, 178, 0.12)",
  "rgba(219, 39, 119, 0.12)",
  "rgba(107, 114, 128, 0.12)",
];

const inRange = (value, start, end) => {
  if (!value) return false;
  return (value.isSame(start) || value.isAfter(start)) && (value.isSame(end) || value.isBefore(end));
};

export async function loader() {
  const animals = await listAnimals();
  const assessments = await listAssessments();
  const changes = await listChanges();
  const finalEvaluations = await listFinalEvaluations();
  const sectionAs = await listSectionAs();
  const sectionBs = await listSectionBs();
  const categories = await categoryList();
  const types = await typeList();
  const statuses = await statusList();

  return {
    animals,
    assessments,
    changes,
    finalEvaluations,
    sectionAs,
    sectionBs,
    categories,
    types,
    statuses,
  };
}

export default function TimelinePage({ title = "Timeline" }) {
  const {
    animals,
    assessments,
    changes,
    finalEvaluations,
    sectionAs,
    sectionBs,
    categories,
    types,
    statuses,
  } = useLoaderData();

  const compositeDefinitions = React.useMemo(() => ([
    {
      key: "positive_calm",
      label: "Positive Calm",
      getValue: ({ sectionAs: sectionARecords }) => averageAdjectiveComposite(sectionARecords, ["content", "calm", "comfortable", "at_ease", "relaxed"]),
    },
    {
      key: "negative_calm",
      label: "Negative Calm",
      getValue: ({ sectionAs: sectionARecords }) => averageAdjectiveComposite(sectionARecords, ["withdrawn", "tense", "listless", "wary", "indifferent"]),
    },
    {
      key: "positive_active",
      label: "Positive Active",
      getValue: ({ sectionAs: sectionARecords }) => averageAdjectiveComposite(sectionARecords, ["lively", "playful", "sociable", "curious", "excited"]),
    },
    {
      key: "negative_active",
      label: "Negative Active",
      getValue: ({ sectionAs: sectionARecords }) => averageAdjectiveComposite(sectionARecords, ["frustrated", "irritable", "agitated", "anxious", "fearful"]),
    },
  ]), []);

  const sectionBScoreOptions = React.useMemo(() =>
    sectionBBehaviors.map((behavior) => ({
      value: `sectionb_${behavior.behaviorKey}`,
      label: `B: ${behavior.title}`,
    })),
  []);

  const scoreOptions = React.useMemo(() => ([
    { value: "overall_welfare", label: "Overall welfare" },
    { value: "mental_health", label: "Mental health" },
    { value: "physical_health", label: "Physical health" },
    ...compositeDefinitions.map((definition) => ({
      value: definition.key,
      label: definition.label,
    })),
    ...sectionBScoreOptions,
  ]), [compositeDefinitions, sectionBScoreOptions]);

  const methods = useForm({
    defaultValues: {
      lookback_days: 90,
      animal_ids: [],
      score_keys: ["overall_welfare"],
    },
  });
  const { control } = methods;
  const formValues = useWatch({ control });
  const lookbackDaysRaw = Number(formValues?.lookback_days);
  const lookbackDays = Number.isFinite(lookbackDaysRaw) && lookbackDaysRaw > 0
    ? Math.floor(lookbackDaysRaw)
    : 90;
	const selectedAnimalIds = React.useMemo(() => { return formValues?.animal_ids || [] }, [formValues]);
	const selectedScoreKeys = React.useMemo(() => { return formValues?.score_keys || ["overall_welfare"] }, [formValues]);
  const selectedAnimalIdSet = React.useMemo(
    () => new Set(selectedAnimalIds.map((id) => String(id))),
    [selectedAnimalIds]
  );
  const selectedScoreKeySet = React.useMemo(
    () => new Set(selectedScoreKeys),
    [selectedScoreKeys]
  );

  const rangeEnd = React.useMemo(() => today().endOf("day"), []);
  const lookbackStart = React.useMemo(
    () => today().subtract(lookbackDays, "day").startOf("day"),
    [lookbackDays]
  );

  const filteredAssessments = React.useMemo(() => assessments.filter((assessment) => {
    if (assessment.is_visibile_in_reports === false) return false;
    if (assessment.submitted_on === null || !assessment.started_on) return false;
    if (!inRange(assessment.started_on, lookbackStart, rangeEnd)) return false;
    if (!selectedAnimalIdSet.size) return true;
    return selectedAnimalIdSet.has(String(assessment.animal_id));
  }), [assessments, lookbackStart, rangeEnd, selectedAnimalIdSet]);

  const filteredChanges = React.useMemo(() => changes.filter((change) => {
    const hasStart = change.start && inRange(change.start, lookbackStart, rangeEnd);
    const hasEnd = change.end && inRange(change.end, lookbackStart, rangeEnd);
    return hasStart || hasEnd;
  }), [changes, lookbackStart, rangeEnd]);

  const rangeStart = React.useMemo(() => {
    const earliestCandidates = [];
    filteredAssessments.forEach((assessment) => {
      if (assessment.started_on) earliestCandidates.push(assessment.started_on);
    });
    filteredChanges.forEach((change) => {
      if (change.start) earliestCandidates.push(change.start);
      if (change.end) earliestCandidates.push(change.end);
    });
    if (!earliestCandidates.length) return lookbackStart;
    return earliestCandidates.reduce((earliest, current) => (
      current.isBefore(earliest) ? current : earliest
    ));
  }, [filteredAssessments, filteredChanges, lookbackStart]);

  const animalLookup = React.useMemo(() => new Map(
    animals.map((animal) => [animal.id, animal.name || "Unknown"])
  ), [animals]);

  const finalEvalLookup = React.useMemo(() => new Map(
    finalEvaluations.map((evaluation) => [evaluation.id, evaluation])
  ), [finalEvaluations]);
  const sectionALookup = React.useMemo(() => {
    const map = new Map();
    (sectionAs || []).forEach((record) => {
      if (!map.has(record.assessment_id)) {
        map.set(record.assessment_id, []);
      }
      map.get(record.assessment_id).push(record);
    });
    return map;
  }, [sectionAs]);
  const sectionBLookup = React.useMemo(() => {
    const map = new Map();
    (sectionBs || []).forEach((record) => {
      if (!map.has(record.assessment_id)) {
        map.set(record.assessment_id, []);
      }
      map.get(record.assessment_id).push(record);
    });
    return map;
  }, [sectionBs]);

  const labels = React.useMemo(() => {
    const start = dayjs(rangeStart);
    const end = dayjs(rangeEnd);
    const output = [];
    let cursor = start;
    while (cursor.isSame(end) || cursor.isBefore(end)) {
      output.push(date(cursor));
      cursor = cursor.add(1, "day");
    }
    return output;
  }, [rangeStart, rangeEnd]);

  const { datasets, changeRanges } = React.useMemo(() => {
    const seriesScores = new Map();
    const scoreLabelLookup = new Map(scoreOptions.map((option) => [option.value, option.label]));

    filteredAssessments.forEach((assessment) => {
      const finalEvaluation = finalEvalLookup.get(assessment.id);
      const sectionARecords = sectionALookup.get(assessment.id) || [];
      const sectionBRecords = sectionBLookup.get(assessment.id) || [];
      const scoreContext = { finalEvaluation, sectionAs: sectionARecords, sectionBs: sectionBRecords };

      selectedScoreKeys.forEach((scoreKey) => {
        if (!selectedScoreKeySet.has(scoreKey)) return;
        let scoreValue = null;
        if (scoreKey === "overall_welfare") {
          const rawScore = finalEvaluation?.overall_welfare;
          const score = typeof rawScore === "number" ? rawScore : Number(rawScore);
          scoreValue = Number.isFinite(score) ? score : null;
        } else if (scoreKey === "mental_health") {
          const rawScore = finalEvaluation?.mental_health;
          const score = typeof rawScore === "number" ? rawScore : Number(rawScore);
          scoreValue = Number.isFinite(score) ? score : null;
        } else if (scoreKey === "physical_health") {
          const rawScore = finalEvaluation?.physical_health;
          const score = typeof rawScore === "number" ? rawScore : Number(rawScore);
          scoreValue = Number.isFinite(score) ? score : null;
        } else if (scoreKey.startsWith("sectionb_")) {
          const behaviorKey = scoreKey.slice("sectionb_".length);
          const frequencyData = sectionBBehaviorFrequencyPercents(sectionBRecords);
          const percent = frequencyData.behaviors?.[behaviorKey]?.percent ?? 0;
          scoreValue = percent / 10;
        } else {
          const definition = compositeDefinitions.find((item) => item.key === scoreKey);
          if (definition) {
            scoreValue = definition.getValue(scoreContext);
          }
        }

        if (scoreValue === null) return;

        const dayKey = date(assessment.started_on);
        const seriesKey = `${assessment.animal_id}::${scoreKey}`;
        const animalLabel = animalLookup.get(assessment.animal_id) || "Unknown";
        const scoreLabel = scoreLabelLookup.get(scoreKey) || scoreKey;
        if (!seriesScores.has(seriesKey)) {
          seriesScores.set(seriesKey, {
            animalId: assessment.animal_id,
            scoreKey,
            animalLabel,
            scoreLabel,
            label: `${animalLabel} - ${scoreLabel}`,
            points: new Map(),
          });
        }
        const series = seriesScores.get(seriesKey);
        const existing = series.points.get(dayKey);
        if (!existing || assessment.started_on.isAfter(existing.started_on)) {
          series.points.set(dayKey, { score: scoreValue, started_on: assessment.started_on });
        }
      });
    });

    const series = [];
    let colorIndex = 0;
    seriesScores.forEach((entry) => {
      const color = palette[colorIndex % palette.length];
      colorIndex += 1;
      const data = labels.map((label) => {
        const entryPoint = entry.points.get(label);
        return entryPoint ? entryPoint.score : null;
      });
      series.push({
        label: entry.label,
        animalLabel: entry.animalLabel,
        scoreLabel: entry.scoreLabel,
        data,
        borderColor: color,
        backgroundColor: color,
        tension: 0.2,
        fill: false,
        spanGaps: true,
        pointRadius: 3,
      });
    });

    const labelIndex = new Map(labels.map((label, index) => [label, index]));
    const ranges = [];
    filteredChanges.forEach((change) => {
      if (!change.start) return;

      const status = change.status || "";
      let startDate = change.start;
      let endDate = change.end;

      if (status === "single_event") {
        endDate = change.start;
      } else if (status === "ongoing") {
        endDate = change.end || rangeEnd;
      }

      if (!endDate) {
        endDate = change.start;
      }

      const startLabel = date(startDate);
      const endLabel = date(endDate);
      let startIndex = labelIndex.get(startLabel);
      let endIndex = labelIndex.get(endLabel);
      if (startIndex === undefined || endIndex === undefined) return;
      const clampedStart = Math.max(0, Math.min(startIndex, labels.length - 1));
      const clampedEnd = Math.max(0, Math.min(endIndex, labels.length - 1));
      startIndex = Math.min(clampedStart, clampedEnd);
      endIndex = Math.max(clampedStart, clampedEnd);

      ranges.push({
        start: startIndex,
        end: endIndex,
        change,
        startLabel,
        endLabel,
      });
    });

    return { datasets: series, changeRanges: ranges };
  }, [
    filteredAssessments,
    filteredChanges,
    finalEvalLookup,
    sectionALookup,
    sectionBLookup,
    animalLookup,
    labels,
    rangeEnd,
    selectedScoreKeys,
    selectedScoreKeySet,
    compositeDefinitions,
    scoreOptions,
  ]);

  const [hiddenChangeIds, setHiddenChangeIds] = React.useState(() => new Set());
  const [filtersOpen, setFiltersOpen] = React.useState(false);

  React.useEffect(() => {
    setHiddenChangeIds((prev) => {
      const allowedIds = new Set(filteredChanges.map((change) => change.id));
      const next = new Set();
      prev.forEach((id) => {
        if (allowedIds.has(id)) next.add(id);
      });
      return next;
    });
  }, [filteredChanges]);

  const visibleChangeRanges = React.useMemo(
    () => changeRanges.filter((range) => !hiddenChangeIds.has(range.change.id)),
    [changeRanges, hiddenChangeIds]
  );
  const changeFillColorById = React.useMemo(() => {
    const map = new Map();
    filteredChanges.forEach((change, index) => {
      map.set(change.id, changeRangePalette[index % changeRangePalette.length]);
    });
    return map;
  }, [filteredChanges]);

  const toggleChange = React.useCallback((changeId) => {
    setHiddenChangeIds((prev) => {
      const next = new Set(prev);
      if (next.has(changeId)) {
        next.delete(changeId);
      } else {
        next.add(changeId);
      }
      return next;
    });
  }, []);

  const changeRangePlugin = React.useMemo(() => ({
    id: "changeRangeShading",
    beforeDatasetsDraw: (chart) => {
      if (!visibleChangeRanges.length) return;
      const { ctx, chartArea, scales } = chart;
      const xScale = scales.x;
      if (!xScale) return;
      ctx.save();
      visibleChangeRanges.forEach((range) => {
        ctx.fillStyle = changeFillColorById.get(range.change.id) || "rgba(217, 119, 6, 0.12)";
        const startPixel = xScale.getPixelForValue(range.start);
        const endPixel = xScale.getPixelForValue(range.end);
        const step = Number.isFinite(xScale.getPixelForValue(range.start + 1))
          ? xScale.getPixelForValue(range.start + 1) - startPixel
          : 0;
        const left = Math.min(startPixel - step / 2, endPixel - step / 2);
        const right = Math.max(startPixel + step / 2, endPixel + step / 2);
        ctx.fillRect(left, chartArea.top, right - left, chartArea.bottom - chartArea.top);
      });
      ctx.restore();
    },
    afterEvent: (chart, args) => {
      const { event } = args;
      if (!event) return;
      if (!["mousemove", "mouseout", "touchmove", "click"].includes(event.type)) return;

      if (event.type === "mouseout") {
        chart.$changeTooltip = null;
        chart.draw();
        return;
      }

      const { chartArea, scales } = chart;
      if (!chartArea || event.x < chartArea.left || event.x > chartArea.right) {
        if (chart.$changeTooltip) {
          chart.$changeTooltip = null;
          chart.draw();
        }
        return;
      }

      const xScale = scales.x;
      if (!xScale) return;
      const index = Math.round(xScale.getValueForPixel(event.x));
      const activeRanges = visibleChangeRanges.filter((range) => index >= range.start && index <= range.end);

      if (!activeRanges.length) {
        if (chart.$changeTooltip) {
          chart.$changeTooltip = null;
          chart.draw();
        }
        return;
      }

      chart.$changeTooltip = {
        x: event.x,
        y: Math.max(chartArea.top + 8, event.y),
        ranges: activeRanges,
      };
      chart.draw();
    },
    afterDraw: (chart) => {
      const tooltip = chart.$changeTooltip;
      if (!tooltip) return;
      const { ctx, chartArea } = chart;
      const lines = [];

      tooltip.ranges.forEach((range) => {
        const change = range.change;
        const animalsText = (change.animals || [])
          .map((id) => animalLookup.get(id))
          .filter(Boolean)
          .join(", ");
        const parts = [
          listLabel(change.category || "Change", categories),
          listLabel(change.change_type || "", types),
          listLabel(change.status || "", statuses),
        ].filter(Boolean);
        const summary = parts.join(" · ");
        const dateRange = range.startLabel === range.endLabel
          ? range.startLabel
          : `${range.startLabel} - ${range.endLabel}`;
        const suffix = change.description ? ` — ${change.description}` : "";
        const animalsSuffix = animalsText ? ` (Animals: ${animalsText})` : "";
        lines.push(`${summary} (${dateRange})${animalsSuffix}${suffix}`);
      });

      const padding = 8;
      ctx.save();
      ctx.font = "12px sans-serif";
      const textWidths = lines.map((line) => ctx.measureText(line).width);
      const boxWidth = Math.min(
        Math.max(...textWidths, 120) + padding * 2,
        chartArea.right - chartArea.left - 8
      );
      const lineHeight = 16;
      const boxHeight = lines.length * lineHeight + padding * 2;
      const x = Math.min(tooltip.x + 10, chartArea.right - boxWidth);
      const y = Math.min(tooltip.y + 10, chartArea.bottom - boxHeight);

      ctx.fillStyle = "rgba(17, 24, 39, 0.9)";
      ctx.strokeStyle = "rgba(255, 255, 255, 0.15)";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.roundRect(x, y, boxWidth, boxHeight, 6);
      ctx.fill();
      ctx.stroke();

      ctx.fillStyle = "white";
      lines.forEach((line, idx) => {
        const textY = y + padding + lineHeight * (idx + 0.9);
        ctx.fillText(line, x + padding, textY, boxWidth - padding * 2);
      });
      ctx.restore();
    },
  }), [visibleChangeRanges, changeFillColorById, animalLookup, categories, statuses, types]);

  const formatChangeLabel = React.useCallback((change) => {
    const parts = [
      listLabel(change.category || "Change", categories),
      listLabel(change.change_type || "", types),
      listLabel(change.status || "", statuses),
    ].filter(Boolean);
    const summary = parts.join(" · ");
    const animalsText = (change.animals || [])
      .map((id) => animalLookup.get(id))
      .filter(Boolean)
      .join(", ");
    const timeframe = change.start && change.end
      ? `${date(change.start)} - ${date(change.end)}`
      : change.start
        ? date(change.start)
        : "Date unknown";
    const animalsSuffix = animalsText ? ` (Animals: ${animalsText})` : "";
    return `${summary} (${timeframe})${animalsSuffix}`;
  }, [animalLookup, categories, statuses, types]);

  const chartData = {
    labels,
    datasets,
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        mode: "index",
        intersect: false,
        callbacks: {
          label: (context) => {
            return `${context.dataset.label}: ${context.formattedValue}`;
          },
        },
      },
    },
    scales: {
      y: {
        min: 0,
        max: 10,
        ticks: { stepSize: 1 },
      },
      x: {
        ticks: { autoSkip: true, maxTicksLimit: 12 },
      },
    },
  };

  const legendGroups = React.useMemo(() => {
    const groups = new Map();
    datasets.forEach((dataset) => {
      const animalLabel = dataset.animalLabel || "Unknown";
      if (!groups.has(animalLabel)) {
        groups.set(animalLabel, []);
      }
      groups.get(animalLabel).push({
        scoreLabel: dataset.scoreLabel || dataset.label,
        color: dataset.borderColor,
      });
    });
    return Array.from(groups.entries()).map(([animalLabel, scores]) => ({
      animalLabel,
      scores,
    }));
  }, [datasets]);

  return (
    <Container component="main" maxWidth="lg" sx={{ pb: 4 }}>
      <Header label={title} />
      <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 1.5, sm: 2 }, minWidth: 0 }}>
        <Typography variant="body2">
          Timeline shows overall welfare scores from completed assessments for the last 90 days,
          with change dates highlighted.
        </Typography>
        <Divider />
        <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: { xs: 0.5, sm: 2 } }}>
          <Typography variant="body2">
            Range: {date(rangeStart)} to {date(rangeEnd)}
          </Typography>
          <Typography variant="body2">
            Assessments: {filteredAssessments.length} | Changes: {filteredChanges.length} | Animals: {animals.length}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <IconButton
            onClick={() => setFiltersOpen((open) => !open)}
            aria-label={filtersOpen ? "Hide filters" : "Show filters"}
            color={filtersOpen ? "primary" : "default"}
            size="small"
          >
            <FilterAltIcon />
          </IconButton>
          <Typography variant="body2" sx={{ color: filtersOpen ? "primary.main" : "text.secondary" }}>
            Filters
          </Typography>
        </Box>
        <Collapse in={filtersOpen} timeout="auto" unmountOnExit>
          <FormProvider {...methods}>
            <Form onChange={() => {}}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "180px 1fr", md: "180px 1fr 1fr" },
                  gap: 2,
                  alignItems: "start",
                  width: "100%",
                  minWidth: 0,
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <FormTextField
                    name="lookback_days"
                    label="Lookback Days"
                    rules={{
                      pattern: {
                        value: /^[0-9]+$/,
                        message: "Only whole numbers are allowed",
                      },
                    }}
                  />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <FormMultiSelectChipsFilter
                    name="animal_ids"
                    label="Animals"
                    enableSelectAll
                    list={animals.map((cur) => ({ label: animalName(cur), value: cur.id }))}
                    enableTextFilter={true}
                    enableSelectFilter={true}
                    selectFilterList={animals.reduce((acc, cur) => {
                      if (!acc.includes(cur.species)) acc.push(cur.species);
                      return acc;
                    }, []).map((cur) => ({ value: cur, label: speciesName(cur) }))}
                  />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <FormMultiSelectChips
                    name="score_keys"
                    label="Scores"
                    enableSelectAll
                    list={scoreOptions}
                  />
                </Box>
              </Box>
            </Form>
          </FormProvider>
          {filteredChanges.length > 0 && (
            <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 1, maxHeight: { xs: 260, sm: "none" }, overflow: { xs: "auto", sm: "visible" } }}>
              {filteredChanges.map((change) => (
                <FormControlLabel
                  key={change.id}
                  control={
                    <Checkbox
                      checked={!hiddenChangeIds.has(change.id)}
                      onChange={() => toggleChange(change.id)}
                      size="small"
                    />
                  }
                  label={(
                    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                      <Box component="span" sx={{ fontSize: "0.875rem", lineHeight: 1.43 }}>
                        {formatChangeLabel(change)}
                      </Box>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: 0.5,
                          border: "1px solid rgba(0, 0, 0, 0.35)",
                          backgroundColor: changeFillColorById.get(change.id) || "rgba(217, 119, 6, 0.12)",
                        }}
                      />
                    </Box>
                  )}
                  sx={{
                    alignItems: "center",
                    "& .MuiCheckbox-root": { py: 0.5 },
                    "& .MuiFormControlLabel-label": {
                      whiteSpace: "normal",
                      display: "inline-flex",
                      alignItems: "center",
                    },
                  }}
                />
              ))}
            </Box>
          )}
        </Collapse>
        <Box
          sx={{
            mt: 2,
            height: { xs: 320, sm: 380, md: 420 },
            width: "100%",
            minWidth: 0,
            maxWidth: "100%",
            position: "relative",
            overflow: "hidden",
            "& canvas": {
              width: "100% !important",
              maxWidth: "100% !important",
            },
          }}
        >
          <Line
            key={`timeline-chart-${lookbackDays}-${selectedAnimalIds.join("-")}-${selectedScoreKeys.join("-")}-${dayjs(rangeStart).valueOf()}`}
            data={chartData}
            options={chartOptions}
            plugins={[changeRangePlugin]}
            redraw
            style={{ width: "100%", minWidth: 0 }}
          />
        </Box>
        {legendGroups.length > 0 && (
          <Box sx={{ mt: 2, display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(auto-fit, minmax(180px, 1fr))" }, gap: 2 }}>
            {legendGroups.map((group) => (
              <Box key={group.animalLabel}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {group.animalLabel}
                </Typography>
                {group.scores.map((score) => (
                  <Box key={score.scoreLabel} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                    <Box sx={{ width: 16, height: 8, backgroundColor: score.color, borderRadius: 1 }} />
                    <Typography variant="body2">{score.scoreLabel}</Typography>
                  </Box>
                ))}
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Container>
  );
}
